import { useRef } from "react";
import { etichettaGiorno } from "./api";

type Props = {
  /** Nome del gruppo per chi usa un lettore di schermo. */
  etichetta: string;
  giorni: string[];
  oggi: string;
  valore: string | null;
  onChange: (giorno: string | null) => void;
  /** Aggiunge «Nessun ritiro» in coda (dialog del reso). */
  conNessuno?: boolean;
  disabled?: boolean;
};

const BASE =
  "rounded-md border text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:cursor-not-allowed";
const SPENTO = "border-line-2 bg-card text-ink hover:bg-paper";
const ACCESO = "border-ink bg-ink text-on-ink";

/** Indice successivo secondo il pattern APG «radiogroup» (frecce/Home/End, con
 *  giro agli estremi). ``null`` = tasto non gestito qui. */
function prossimoIndice(attuale: number, key: string, lunghezza: number): number | null {
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return (attuale + 1) % lunghezza;
    case "ArrowLeft":
    case "ArrowUp":
      return (attuale - 1 + lunghezza) % lunghezza;
    case "Home":
      return 0;
    case "End":
      return lunghezza - 1;
    default:
      return null;
  }
}

/** I prossimi giorni lavorativi come tasti: un click, nessun calendario da aprire.
 *  I giorni arrivano dal backend (regola delle 11:50 in un posto solo).
 *
 *  Tastiera: roving tabindex, pattern APG «radiogroup» a selezione automatica
 *  — un solo tab-stop (l'opzione scelta, o la prima se nessuna lo è ancora).
 *  Le frecce spostano il focus E selezionano (come un click), Home/End vanno
 *  agli estremi, con giro. «Nessun ritiro» è l'ultima opzione della sequenza. */
export function StrisciaGiorni({
  etichetta,
  giorni,
  oggi,
  valore,
  onChange,
  conNessuno,
  disabled,
}: Props) {
  const opzioni: (string | null)[] = conNessuno ? [...giorni, null] : giorni;
  const selezionato = opzioni.findIndex((v) => v === valore);
  const tabStop = selezionato === -1 ? 0 : selezionato;
  const bottoni = useRef<(HTMLButtonElement | null)[]>([]);

  const suTasto = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (disabled) return;
    const next = prossimoIndice(index, event.key, opzioni.length);
    if (next === null) return;
    event.preventDefault();
    onChange(opzioni[next] ?? null);
    bottoni.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={etichetta} className="flex flex-wrap gap-1.5">
      {opzioni.map((val, index) => {
        const scelto = val === valore;
        const tabIndex = index === tabStop ? 0 : -1;
        if (val === null) {
          return (
            <button
              key="nessuno"
              ref={(el) => {
                bottoni.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={scelto}
              aria-label="Nessun ritiro"
              tabIndex={tabIndex}
              disabled={disabled}
              onClick={() => onChange(null)}
              onKeyDown={(e) => suTasto(e, index)}
              className={`${BASE} px-3 text-[12px] font-semibold ${scelto ? ACCESO : SPENTO}`}
            >
              Nessun ritiro
            </button>
          );
        }
        const { sopra, numero } = etichettaGiorno(val, oggi);
        return (
          <button
            key={val}
            ref={(el) => {
              bottoni.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={scelto}
            aria-label={`${sopra} ${numero}`}
            tabIndex={tabIndex}
            disabled={disabled}
            onClick={() => onChange(val)}
            onKeyDown={(e) => suTasto(e, index)}
            className={`${BASE} w-[52px] py-1 ${scelto ? ACCESO : SPENTO}`}
          >
            <span className="block text-[11px] lowercase">{sopra}</span>
            <span className="block font-display text-[17px] leading-tight font-bold">{numero}</span>
          </button>
        );
      })}
    </div>
  );
}
